/**
 * NavigatorStatus — facilitator view of Navigator conversations.
 * Matches DashboardSection pattern (bg-card, border, p-5, font-heading text-h3).
 */

import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

interface ParticipantNav {
  role_id: string
  character_name: string
  country_code: string
  intro_done: boolean
  outro_done: boolean
  mid_count: number
  active: boolean
}

export function NavigatorStatus({ simId }: { simId: string }) {
  const [participants, setParticipants] = useState<ParticipantNav[]>([])
  const [expanded, setExpanded] = useState(false)

  const load = useCallback(async () => {
    const { data: roles } = await supabase
      .from('roles')
      .select('id,character_name,country_code,user_id')
      .eq('sim_run_id', simId)
      .not('user_id', 'is', null)

    if (!roles?.length) { setParticipants([]); return }

    const { data: convos } = await supabase
      .from('navigator_conversations')
      .select('role_id,conversation_type,completion_status')
      .eq('sim_run_id', simId)

    const convoList = convos || []

    const result: ParticipantNav[] = roles.map(r => {
      const mine = convoList.filter(c => c.role_id === r.id)
      return {
        role_id: r.id,
        character_name: r.character_name,
        country_code: r.country_code,
        intro_done: mine.some(c => c.conversation_type === 'intro' && c.completion_status === 'completed'),
        outro_done: mine.some(c => c.conversation_type === 'outro' && c.completion_status === 'completed'),
        mid_count: mine.filter(c => c.conversation_type === 'mid' && c.completion_status === 'completed').length,
        active: mine.some(c => c.completion_status === 'in_progress'),
      }
    })

    setParticipants(result.sort((a, b) => a.character_name.localeCompare(b.character_name)))
  }, [simId])

  useEffect(() => {
    load()
    const ch = supabase.channel(`nav-status-${simId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'navigator_conversations', filter: `sim_run_id=eq.${simId}` }, () => load())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId, load])

  const introCount = participants.filter(p => p.intro_done).length
  const outroCount = participants.filter(p => p.outro_done).length
  const activeCount = participants.filter(p => p.active).length
  const total = participants.length

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-heading text-h3 text-text-primary">Navigator</h3>
        {activeCount > 0 && (
          <span className="w-2 h-2 rounded-full bg-success animate-pulse" title={`${activeCount} speaking`} />
        )}
        <div className="ml-auto flex items-center gap-4 font-data text-caption text-text-secondary">
          <span>Intro <strong className="text-text-primary">{introCount}</strong>/{total}</span>
          <span>Outro <strong className="text-text-primary">{outroCount}</strong>/{total}</span>
        </div>
      </div>

      {total === 0 ? (
        <p className="font-body text-caption text-text-secondary">No participants assigned yet.</p>
      ) : (
        <>
          <div className={`space-y-1.5 ${expanded ? '' : 'max-h-[120px] overflow-hidden relative'}`}>
            {participants.map(p => (
              <div key={p.role_id} className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {p.active && <span className="w-2 h-2 rounded-full bg-success animate-pulse flex-shrink-0" />}
                  <span className="font-body text-body-sm text-text-primary truncate">{p.character_name}</span>
                  <span className="font-data text-caption text-text-secondary/50">{p.country_code}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {p.intro_done && <span className="font-data text-[10px] bg-success/15 text-success px-1.5 py-0.5 rounded">Intro</span>}
                  {p.mid_count > 0 && <span className="font-data text-[10px] bg-action/15 text-action px-1.5 py-0.5 rounded">{p.mid_count} mid</span>}
                  {p.outro_done && <span className="font-data text-[10px] bg-accent/15 text-accent px-1.5 py-0.5 rounded">Outro</span>}
                  {!p.intro_done && !p.outro_done && p.mid_count === 0 && (
                    <span className="font-data text-[10px] text-text-secondary/30">—</span>
                  )}
                </div>
              </div>
            ))}
            {!expanded && participants.length > 4 && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent" />
            )}
          </div>
          {participants.length > 4 && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="font-body text-caption text-action hover:underline mt-2"
            >
              {expanded ? 'Show less' : `Show all ${participants.length} participants`}
            </button>
          )}
        </>
      )}
    </section>
  )
}
